import { html, LitElement, type TemplateResult, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { sentryCaptureException } from './_ui-core_/base/package/services/package/sentry/sentry-client';
import { initFlavor } from './_ui-core_/base/package/util/flavor';
import { idleTick } from './_ui-core_/base/package/util/render';
import App from './app';
import { type SidebarEvent, SidebarEventName } from './app/package/base/router/router-types/sidebar-types';
import type { RenderState } from './app/package/base/service/render/render-service';
import styles from './app-root.css?inline';
import './page-templates/base-page/base-page';
import './page-templates/blank-page/blank-page';
import './page-templates/cat-filter-page/cat-filter-page';
import './pages/unload/page';

export type RenderRootCallbacks = {
  add: (component: TemplateResult) => void;
  get: () => TemplateResult;
  has: () => boolean;
  set: (page: TemplateResult) => void;
  unload: () => void;
};

@customElement('app-root')
export class AppRoot extends LitElement {
  static readonly styles = unsafeCSS(styles);

  @state() private _root: TemplateResult;
  @state() private readonly _persistentComponents: TemplateResult[] = [];

  private _cleanupTaskId: number | null = null;

  private readonly _renderStoreCallback = (renderStatus: RenderState) => {
    if (renderStatus === 'visible') {
      // Unsubscribe from further updates once the app is visible
      window.$app.renderService.renderStore.unsubscribe(this._renderStoreCallback);
      this._removeSplash();
      if (window.$app.appConfig.prerenderMode) {
        this._cleanupForStaticPageRender();
      } else {
        this._cleanupTaskId = window.$app.prerenderService.removeTask(this._cleanupTaskId);
      }
    }
  };

  constructor() {
    super();
    window.$app.perf.setTag('appRoot');
    this._cleanupTaskId = window.$app.prerenderService.addTask('app-root-cleanup');

    if ((import.meta as any).env.DEV) {
      //` Catch unhandled promise rejections
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        window.$app.logger.error('Unhandled Promise Rejection', event.reason, { event });
        sentryCaptureException(event);
      });

      // Catch unhandled errors
      window.addEventListener('error', (event: ErrorEvent) => {
        window.$app.logger.error('Global Error Caught', event.error, { event });
        sentryCaptureException(event);
      });
    }

    // Singleton — lives for app lifetime, no cleanup needed (accepted pattern exception)
    window.addEventListener(SidebarEventName, (ev: Event) => {
      this._checkSidebarEvent(ev as CustomEvent<SidebarEvent>);
    });
  }

  public hasDisplay(): boolean {
    return this._root !== undefined;
  }

  public getDisplay(): TemplateResult {
    return this._root;
  }

  public setDisplay(route: TemplateResult): void {
    this._root = route;
  }

  /**
   * Unload the current page and immediately display a blank page.
   * Usage example: links to external pages, or when the user logs out.
   */
  public unloadDisplay(): void {
    this._root = html`<unload-page></unload-page>`;
  }

  public addPersistentComponent(component: TemplateResult): void {
    this._persistentComponents.push(component);
    // Manually triggering an update because we are only mutating the data
    this.requestUpdate();
  }

  connectedCallback() {
    super.connectedCallback();

    App.ready.then(() => {
      // Handle flavor
      const isReloadInitialized = initFlavor();
      if (isReloadInitialized) {
        // Page reload initiated to apply flavor, stop further initialization
        window.$app.logger.log('Awaiting flavor reload…');
        return;
      }

      window.$app.logger.log('initializing…');
      const display: RenderRootCallbacks = {
        add: this.addPersistentComponent.bind(this),
        get: this.getDisplay.bind(this),
        has: this.hasDisplay.bind(this),
        set: this.setDisplay.bind(this),
        unload: this.unloadDisplay.bind(this),
      };
      App.instance.initialize(display);
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    window.$app.renderService.renderStore.unsubscribe(this._renderStoreCallback);
  }

  firstUpdated() {
    window.$app.renderService.renderStore.subscribe(this._renderStoreCallback);
  }

  render() {
    return html`${this._root}${this._persistentComponents.map((component) => component)}`;
  }

  private async _cleanupForStaticPageRender(): Promise<void> {
    await idleTick();
    // Remove module preload meta tags from HTML head as they are no longer needed
    // and should not be stored in the DOM.
    const modulePreloadTags = document.querySelectorAll('link[rel="modulepreload"]');
    modulePreloadTags.forEach((tag) => {
      tag.remove();
    });
    // Remove ui-core styles to reduce final HTML size
    this._removeUiCoreStyles();
    this._cleanupTaskId = window.$app.prerenderService.removeTask(this._cleanupTaskId);
  }

  private _removeUiCoreStyles(): void {
    const styleTags: NodeListOf<HTMLStyleElement> = document.querySelectorAll('style[media="ui-core"]');
    styleTags.forEach((styleTag: HTMLStyleElement) => {
      styleTag.remove();
    });
    window.$app.logger.log(`removed ${styleTags.length} style tags with media="ui-core"`);
  }

  private _removeSplash(): void {
    const splashEl = document.getElementById('splash');
    if (splashEl !== null) {
      splashEl.remove();
    }
  }

  private _checkSidebarEvent(ev: CustomEvent<SidebarEvent>) {
    // TODO: why can this happen?
    if (this._root === undefined) {
      window.$app.logger.log(
        `[app-root] no root component => unhandled sidebar event (${ev.type}) of type: '${ev.detail?.type}'`,
      );
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'app-root': AppRoot;
  }
}
